// Keyboard + pointer-lock mouse input, turned into Quake usercmd fields.

import { DEFAULT_BINDS, RESERVED_KEYS, cloneBinds, type Action, type Binds } from './binds';

export { ACTIONS, DEFAULT_BINDS, assign, bindLabel, findConflicts, keyLabel, type Action, type Binds } from './binds';

/** ET naming: MOUSE1 left, MOUSE2 right, MOUSE3 middle, MOUSE4/5 side buttons. */
export const mouseCode = (button: number): string => `Mouse${[1, 3, 2, 4, 5][button] ?? button + 1}`;

const ONE_SHOT: Action[] = ['save', 'load', 'respawn', 'noclip', 'replay', 'help', 'chat', 'debug', 'voteYes', 'voteNo', 'spectate', 'followNext', 'followPrev', 'thirdPerson', 'save2', 'load2', 'save3', 'load3', 'tjlRecord'];

export class Input {
  private down = new Set<string>();
  /** Keys pressed since the last physics tick, so taps shorter than a tick still count. */
  private tapped = new Set<string>();
  private pendingActions: Action[] = [];
  private mouseDx = 0;
  private mouseDy = 0;
  /** Pending captureNext() request (key rebinding). */
  private capture: ((code: string) => void) | null = null;
  binds: Binds = cloneBinds(DEFAULT_BINDS);
  /** Request raw (unaccelerated) mouse input when locking the pointer. */
  rawInput = true;
  enabled = false;

  constructor(private readonly element: HTMLElement) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('blur', this.clear);
    // capture phase: a pending rebind sees the key before any menu handler
    window.addEventListener('keydown', this.onCaptureKey, true);
    window.addEventListener('mousedown', this.onCaptureMouse, true);
    window.addEventListener('wheel', this.onCaptureWheel, { capture: true, passive: false });
    element.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('blur', this.clear);
    window.removeEventListener('keydown', this.onCaptureKey, true);
    window.removeEventListener('mousedown', this.onCaptureMouse, true);
    window.removeEventListener('wheel', this.onCaptureWheel, true);
  }

  get locked(): boolean {
    return document.pointerLockElement === this.element;
  }

  async lock(): Promise<void> {
    if (this.rawInput) {
      try {
        // raw mouse input where supported (no OS acceleration)
        await (this.element.requestPointerLock as (o?: object) => Promise<void>).call(this.element, { unadjustedMovement: true });
        return;
      } catch {
        /* not supported: plain pointer lock below */
      }
    }
    try {
      await this.element.requestPointerLock();
    } catch {
      /* user gesture required; ignored */
    }
  }

  /**
   * Resolves with the next key, mouse button or wheel direction (for rebinding).
   * Escape resolves "Escape" (cancel); the event never reaches the game or menus.
   */
  captureNext(): Promise<string> {
    this.cancelCapture();
    return new Promise((resolve) => {
      // the click that started listening must not be captured itself
      setTimeout(() => (this.capture = resolve), 0);
    });
  }

  cancelCapture(): void {
    const c = this.capture;
    this.capture = null;
    c?.('Escape');
  }

  get capturing(): boolean {
    return this.capture !== null;
  }

  private finishCapture(e: Event, code: string): void {
    e.preventDefault();
    e.stopImmediatePropagation();
    const c = this.capture;
    this.capture = null;
    c?.(code);
  }

  private onCaptureKey = (e: KeyboardEvent): void => {
    if (this.capture) this.finishCapture(e, RESERVED_KEYS.has(e.code) ? 'Escape' : e.code);
  };

  private onCaptureMouse = (e: MouseEvent): void => {
    if (this.capture) this.finishCapture(e, mouseCode(e.button));
  };

  private onCaptureWheel = (e: WheelEvent): void => {
    if (this.capture && e.deltaY) this.finishCapture(e, e.deltaY < 0 ? 'WheelUp' : 'WheelDown');
  };

  private press(code: string): void {
    if (!this.enabled) return;
    if (!this.down.has(code)) {
      this.tapped.add(code);
      for (const a of ONE_SHOT) if (this.binds[a].includes(code)) this.pendingActions.push(a);
    }
    this.down.add(code);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.enabled) return;
    if (e.code === 'Tab' || e.code === 'Space' || e.code.startsWith('Arrow') || e.code === 'KeyQ' || /^F\d+$/.test(e.code)) e.preventDefault();
    if (e.repeat) return;
    this.press(e.code);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.down.delete(e.code);
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (this.locked) this.press(mouseCode(e.button));
  };

  private onMouseUp = (e: MouseEvent): void => {
    this.down.delete(mouseCode(e.button));
  };

  /** A wheel notch is a tap: active for the next physics tick only. */
  private onWheel = (e: WheelEvent): void => {
    if (!this.locked || !this.enabled || !e.deltaY) return;
    e.preventDefault();
    this.press(e.deltaY < 0 ? 'WheelUp' : 'WheelDown');
    this.down.delete(e.deltaY < 0 ? 'WheelUp' : 'WheelDown');
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.locked || !this.enabled) return;
    this.mouseDx += e.movementX;
    this.mouseDy += e.movementY;
  };

  clear = (): void => {
    this.down.clear();
    this.tapped.clear();
  };

  /** Held (or tapped since the last tick). */
  active(action: Action): boolean {
    const [a, b] = this.binds[action];
    return (!!a && (this.down.has(a) || this.tapped.has(a))) || (!!b && (this.down.has(b) || this.tapped.has(b)));
  }

  /** Call after each physics tick. */
  endTick(): void {
    this.tapped.clear();
  }

  takeMouse(): [number, number] {
    const d: [number, number] = [this.mouseDx, this.mouseDy];
    this.mouseDx = this.mouseDy = 0;
    return d;
  }

  takeActions(): Action[] {
    const a = this.pendingActions;
    this.pendingActions = [];
    return a;
  }

  move(): { forwardmove: number; rightmove: number; upmove: number } {
    const f = (this.active('forward') ? 127 : 0) - (this.active('back') ? 127 : 0);
    const r = (this.active('right') ? 127 : 0) - (this.active('left') ? 127 : 0);
    const u = (this.active('jump') ? 127 : 0) - (this.active('crouch') ? 127 : 0);
    return { forwardmove: f, rightmove: r, upmove: u };
  }
}
